import type { GridApi, IRowNode } from 'ag-grid-community'
import type { GridRuntime } from './runtime'

// Private types ---------------------------------------------------------------

type RenderApi = Pick<
  GridApi<unknown>,
  'getDisplayedRowCount' | 'isAnimationFrameQueueEmpty'
> & {
  readonly getDisplayedRowAtIndex: (
    index: number
  ) => Pick<IRowNode<unknown>, 'data' | 'failedLoad'> | undefined
}

// Public constants ------------------------------------------------------------

export const GRID_READY_EVENT = 'facade:grid-ready'

// Private functions -----------------------------------------------------------

/** Loading rows must keep the placeholder; empty and failed results can appear. */
const hasInitialResult = (api: RenderApi): boolean => {
  const first = api.getDisplayedRowAtIndex(0)

  return (
    api.getDisplayedRowCount() === 0 ||
    first?.data !== undefined ||
    first?.failedLoad === true
  )
}

// Public functions ------------------------------------------------------------

/** Reveal one grid after its first result and queued cell renderers are ready. */
export const createInitialGridReveal = (
  element: HTMLElement,
  runtime: Pick<GridRuntime, 'scheduleFrame'>
): ((api: RenderApi) => void) => {
  let state: 'waiting' | 'rendering' | 'ready' = 'waiting'

  /** Recheck queued work on a frame owned by this grid's cancellable runtime. */
  const finishRender = (api: RenderApi): void => {
    if (!hasInitialResult(api)) {
      state = 'waiting'
      return
    }

    if (!api.isAnimationFrameQueueEmpty()) {
      runtime.scheduleFrame(() => finishRender(api))
      return
    }

    // LiveView may patch host attributes; the ignored child owns readiness.
    element.querySelector('.ag-root-wrapper')?.setAttribute('data-ready', '')
    state = 'ready'
    element.dispatchEvent(new Event(GRID_READY_EVENT, { bubbles: true }))
  }

  return (api) => {
    if (state === 'waiting' && hasInitialResult(api)) {
      state = 'rendering'
      runtime.scheduleFrame(() => finishRender(api))
    }
  }
}
